// 절차 효과음 목소리 배정(순수 로직, c019). WebAudio를 모르고 시각(초)만 받는다. 모든 상태는 생성 때 만든 형식 배열이라
// acquire는 할당하지 않는다. 규칙(묶음 정의는 recipes.CATS):
//  1) 묶음 간격: 같은 묶음 직전 시작 + spacing보다 이르면 그 시각으로 미룬다(maxDelay 넘으면 버린다). 쏟아지는 줍기는 계단처럼 울린다.
//  2) 묶음 상한: 묶음에서 울리는 목소리가 cap개면 그중 우선순위가 가장 낮고(같으면 가장 오래된) 것을 새 소리 우선순위 이하일 때만 끊는다.
//  3) 전체 풀: 빈 목소리가 없으면 전체에서 가장 낮은 우선순위(같으면 가장 오래된) 목소리를 새 소리 우선순위 이하일 때만 끊는다.
//     경고(텔레그래프, 우선순위 9)는 줍기·고유 효과가 아무리 쏟아져도 끊기지 않는다.

export interface PoolCat { readonly cap: number; readonly spacing: number; readonly maxDelay: number }

export const PoolResult = { Ok: 0, Spaced: 1, Cap: 2, Full: 3 } as const;

export class SynthVoicePool {
  readonly size: number;
  private readonly end: Float64Array;
  private readonly start: Float64Array;
  private readonly cat: Int16Array;
  private readonly prio: Int16Array;
  private readonly lastStart: Float64Array;
  /** 마지막 acquire의 시작 시각(미뤄졌으면 now보다 크다) */
  startAt = 0;
  /** 마지막 acquire가 끊은 목소리였는지 */
  stole = false;
  /** 마지막 실패 이유(PoolResult) */
  reason = 0;
  played = 0;
  stolen = 0;
  droppedSpacing = 0;
  droppedCap = 0;
  droppedFull = 0;

  constructor(size: number, private readonly cats: readonly PoolCat[]) {
    this.size = size;
    this.end = new Float64Array(size).fill(-Infinity);
    this.start = new Float64Array(size).fill(-Infinity);
    this.cat = new Int16Array(size).fill(-1);
    this.prio = new Int16Array(size);
    this.lastStart = new Float64Array(cats.length).fill(-Infinity);
  }

  /** 목소리 하나를 잡는다. 성공하면 목소리 번호(startAt에 시작 시각), 실패하면 -1(reason). dur = 재생 길이(초) */
  acquire(cat: number, prio: number, now: number, dur: number): number {
    const C = this.cats[cat];
    this.stole = false;
    let at = now;
    if (C.spacing > 0 && this.lastStart[cat] + C.spacing > now) {
      at = this.lastStart[cat] + C.spacing;
      if (at - now > C.maxDelay + 1e-9) { this.reason = PoolResult.Spaced; this.droppedSpacing++; return -1; }
    }
    // 묶음 상한
    let inCat = 0, catVictim = -1;
    for (let i = 0; i < this.size; i++) {
      if (this.cat[i] !== cat || this.end[i] <= at) continue;
      inCat++;
      if (catVictim < 0 || this.lower(i, catVictim)) catVictim = i;
    }
    let vi = -1;
    if (inCat >= C.cap) {
      if (catVictim < 0 || this.prio[catVictim] > prio) { this.reason = PoolResult.Cap; this.droppedCap++; return -1; }
      vi = catVictim;
    } else {
      // 빈 목소리(가장 먼저 끝난 것) → 없으면 전체 최저 우선순위
      let victim = -1;
      for (let i = 0; i < this.size; i++) {
        if (this.end[i] <= at) { if (vi < 0 || this.end[i] < this.end[vi]) vi = i; continue; }
        if (victim < 0 || this.lower(i, victim)) victim = i;
      }
      if (vi < 0) {
        if (victim < 0 || this.prio[victim] > prio) { this.reason = PoolResult.Full; this.droppedFull++; return -1; }
        vi = victim;
      }
    }
    if (this.end[vi] > at) { this.stole = true; this.stolen++; }
    this.end[vi] = at + dur; this.start[vi] = at; this.cat[vi] = cat; this.prio[vi] = prio;
    this.lastStart[cat] = at;
    this.startAt = at;
    this.reason = PoolResult.Ok;
    this.played++;
    return vi;
  }

  /** a가 b보다 먼저 끊길 목소리인가(우선순위 낮음 → 오래됨) */
  private lower(a: number, b: number): boolean {
    return this.prio[a] < this.prio[b] || (this.prio[a] === this.prio[b] && this.start[a] < this.start[b]);
  }

  /** now에 울리고 있는 묶음 목소리 수 */
  activeIn(cat: number, now: number): number {
    let n = 0;
    for (let i = 0; i < this.size; i++) if (this.cat[i] === cat && this.end[i] > now) n++;
    return n;
  }

  /** 모두 비운다(탭 가림·음소거) */
  clear(): void {
    this.end.fill(-Infinity); this.start.fill(-Infinity); this.cat.fill(-1); this.lastStart.fill(-Infinity);
  }
}
